import * as Sentry from "@sentry/vue"
import type { BaseUser, Company } from "@vitable/asclepius-api-client-ts"
import { UserTypeEnum } from "@vitable/asclepius-api-client-ts"
import { toRaw } from "vue"

import { queryClient } from "@/config/query-client"
import router from "@/router"
import { useCompanyStore, type CompanyContextToken } from "@/store/company-store"
import { useOccupationalHealthStore } from "@/store/occupational-health-store"
import { useSubscriptionStore } from "@/store/subscription-store"
import { useCensusUploadStore } from "@/modules/member-management/stores/census-upload-store"
import { useWorkforceIntegrationsStore } from "@/modules/workforce-integrations/stores/workforce-integrations-store"
import { isEmbeddedContext } from "@common/services/embed-service"

let switchSequence = 0
const VITABLE_ADMIN_EMPLOYER_QUERY_KEY = "employerId"

function resetCompanyScopedStores() {
  useCompanyStore().resetCompanyScopedState()
  useSubscriptionStore().$reset()
  useOccupationalHealthStore().$reset()
  useWorkforceIntegrationsStore().$reset()
  useCensusUploadStore().stopTracking()
}

function captureHydrationFailures(results: PromiseSettledResult<unknown>[]) {
  results.forEach((result) => {
    if (result.status === "rejected") {
      Sentry.captureException(result.reason, { tags: { context: "company_context_hydration" } })
    }
  })
}

function snapshotStoreState<T extends object>(state: T): T {
  return Object.fromEntries(
    Object.entries(toRaw(state)).map(([key, value]) => {
      const rawValue = typeof value === "object" && value !== null ? toRaw(value) : value
      try {
        return [key, structuredClone(rawValue)]
      } catch {
        // Pinia may expose request-state helpers alongside serializable state. They are not reset during a switch.
        return [key, rawValue]
      }
    })
  ) as T
}

function throwOnRequiredHydrationFailure(results: PromiseSettledResult<unknown>[]) {
  captureHydrationFailures(results)
  const failedResult = results.find((result): result is PromiseRejectedResult => result.status === "rejected")
  if (failedResult) throw failedResult.reason
}

// Store actions own fetching and state writes; this coordinator owns the company-context lifecycle across stores.
async function hydrateRequiredCompanyScopedStores(contextToken: CompanyContextToken) {
  const subscriptionStore = useSubscriptionStore()

  const results = await Promise.allSettled([
    subscriptionStore.loadActiveCompanyYouthMembershipPlan(contextToken),
    subscriptionStore.loadActiveCompanySubscription(contextToken),
    subscriptionStore.loadEntitlements(contextToken),
    subscriptionStore.loadBenefits(contextToken),
    subscriptionStore.loadMedicalServices(contextToken)
  ])

  throwOnRequiredHydrationFailure(results)
}

async function hydrateOptionalCompanyScopedStores(contextToken: CompanyContextToken) {
  const companyStore = useCompanyStore()
  const occupationalHealthStore = useOccupationalHealthStore()
  const workforceIntegrationsStore = useWorkforceIntegrationsStore()

  const results = await Promise.allSettled([
    companyStore.loadEmployeesWithMissingInfoCount(contextToken),
    companyStore.loadWasEmployeeCensusUploadedInLast90Days(contextToken),
    companyStore.loadLastEmployeeCensusUploadedDate(contextToken),
    occupationalHealthStore.getNewOhrCount(contextToken),
    workforceIntegrationsStore.loadHrisIntegrations(contextToken).then(async () => {
      if (!companyStore.isCompanyContextCurrent(contextToken)) return

      const workforceResults = await Promise.allSettled([
        workforceIntegrationsStore.loadHrisConnectionStatus(contextToken),
        workforceIntegrationsStore.loadRecentEventCounts(contextToken),
        workforceIntegrationsStore.loadEmployeesWeCannotAddFromHrisCount(contextToken),
        workforceIntegrationsStore.loadEmployeesNotFoundInHrisCount(contextToken)
      ])
      captureHydrationFailures(workforceResults)
    })
  ])

  captureHydrationFailures(results)
}

export function useCompanyContext() {
  const companyStore = useCompanyStore()

  function cancelCompanyContextOperations() {
    switchSequence++
  }

  async function initializeCompanyContext(user: BaseUser) {
    if (isEmbeddedContext()) {
      await companyStore.getCompany()
      const contextToken = companyStore.getCompanyContextToken()
      if (contextToken) {
        // Embedded views only render subscription-backed company data.
        const subscriptionStore = useSubscriptionStore()
        const results = await Promise.allSettled([
          subscriptionStore.loadActiveCompanyYouthMembershipPlan(contextToken),
          subscriptionStore.loadActiveCompanySubscription(contextToken),
          subscriptionStore.loadEntitlements(contextToken),
          subscriptionStore.loadBenefits(contextToken)
        ])
        captureHydrationFailures(results)
      }
      return
    }

    const bootstrapEmployerId = getBootstrapEmployerId(user.user_type)
    if (bootstrapEmployerId === "invalid") {
      companyStore.clearVitableAdminCompany()
      companyStore.setContextStatus("needs-employer", "We couldn't load that employer. Choose another employer.")
      return
    }

    const companyId = companyStore.initializeActiveCompanyId(
      user.user_type,
      user.company_admin?.company_id,
      bootstrapEmployerId
    )
    if (!companyId) return
    const contextToken = companyStore.getCompanyContextToken()
    if (!contextToken) return

    try {
      const company = await companyStore.fetchCompany(companyId)
      if (!companyStore.isCompanyContextCurrent(contextToken)) return

      if (user.user_type === UserTypeEnum.VitableAdmin && !company.active_in) {
        companyStore.clearVitableAdminCompany()
        companyStore.setContextStatus("needs-employer", "That employer is no longer active. Choose another employer.")
        await clearBootstrapEmployerIdIfPresent(bootstrapEmployerId)
        return
      }

      resetCompanyScopedStores()
      if (user.user_type === UserTypeEnum.VitableAdmin) {
        companyStore.commitVitableAdminCompany(company)
      } else {
        companyStore.updateCompany(company)
      }
      await hydrateRequiredCompanyScopedStores(contextToken)
      if (!companyStore.isCompanyContextCurrent(contextToken)) return
      companyStore.setContextStatus("ready")
      void hydrateOptionalCompanyScopedStores(contextToken)

      await clearBootstrapEmployerIdIfPresent(bootstrapEmployerId)
    } catch (error) {
      if (!companyStore.isCompanyContextCurrent(contextToken)) return

      if (user.user_type !== UserTypeEnum.VitableAdmin) {
        companyStore.setContextStatus("error", "We couldn't load your company workspace.")
        throw error
      }

      companyStore.clearVitableAdminCompany()
      companyStore.setContextStatus(
        "needs-employer",
        bootstrapEmployerId
          ? "We couldn't load that employer. Choose another employer."
          : "Your previous employer selection is no longer available."
      )
    }
  }

  async function switchCompany(companyId: string): Promise<boolean> {
    const currentSwitch = ++switchSequence
    const previousCompany = companyStore.company
    if (companyId === companyStore.companyId) {
      companyStore.setContextStatus(previousCompany ? "ready" : "needs-employer")
      return true
    }

    companyStore.setContextStatus("switching")

    let nextCompany: Company
    try {
      nextCompany = await companyStore.fetchCompany(companyId)
    } catch (error) {
      if (currentSwitch !== switchSequence) return false

      Sentry.captureException(error, { tags: { context: "company_context_switch" } })
      companyStore.setContextStatus(
        previousCompany ? "ready" : "needs-employer",
        "We couldn't load that employer. Your current employer has not changed."
      )
      return false
    }

    if (currentSwitch !== switchSequence) return false

    if (!nextCompany.active_in) {
      companyStore.setContextStatus("ready", "That employer is no longer active. Choose another employer.")
      return false
    }

    const subscriptionStore = useSubscriptionStore()
    const occupationalHealthStore = useOccupationalHealthStore()
    const workforceIntegrationsStore = useWorkforceIntegrationsStore()
    const previousCompanyState = snapshotStoreState(companyStore.$state)
    const previousSubscriptionState = snapshotStoreState(subscriptionStore.$state)
    const previousOccupationalHealthState = snapshotStoreState(occupationalHealthStore.$state)
    const previousWorkforceIntegrationsState = snapshotStoreState(workforceIntegrationsStore.$state)
    const previousSessionCompanyId = window.sessionStorage.getItem("gaia-active-company-id")

    companyStore.invalidateCompanyContext()
    resetCompanyScopedStores()
    companyStore.updateCompany(nextCompany)
    const contextToken = companyStore.getCompanyContextToken()
    if (!contextToken) return false

    try {
      await hydrateRequiredCompanyScopedStores(contextToken)
    } catch (error) {
      if (currentSwitch !== switchSequence) return false

      Sentry.captureException(error, { tags: { context: "company_context_switch_hydration" } })
      companyStore.invalidateCompanyContext()
      companyStore.$patch(previousCompanyState)
      subscriptionStore.$patch(previousSubscriptionState)
      occupationalHealthStore.$patch(previousOccupationalHealthState)
      workforceIntegrationsStore.$patch(previousWorkforceIntegrationsState)
      if (previousSessionCompanyId) {
        window.sessionStorage.setItem("gaia-active-company-id", previousSessionCompanyId)
      } else {
        window.sessionStorage.removeItem("gaia-active-company-id")
      }
      companyStore.setContextStatus(
        previousCompany ? "ready" : "needs-employer",
        "We couldn't load that employer. Your current employer has not changed."
      )
      return false
    }

    if (currentSwitch !== switchSequence || !companyStore.isCompanyContextCurrent(contextToken)) return false

    queryClient.clear()
    companyStore.commitVitableAdminCompany(nextCompany)
    companyStore.setContextStatus("ready")
    void hydrateOptionalCompanyScopedStores(contextToken)
    await router.push({ name: "home" })
    return true
  }

  return {
    initializeCompanyContext,
    switchCompany,
    cancelCompanyContextOperations
  }
}

function getBootstrapEmployerId(userType: Optional<UserTypeEnum>): string | null | "invalid" {
  if (userType !== UserTypeEnum.VitableAdmin) return null

  const employerIds = new URLSearchParams(window.location.search).getAll(VITABLE_ADMIN_EMPLOYER_QUERY_KEY)
  if (employerIds.length === 0) return null
  if (employerIds.length !== 1) return "invalid"

  const [employerId] = employerIds
  if (!employerId?.trim()) return "invalid"
  return employerId
}

async function clearBootstrapEmployerId() {
  await router.replace({
    path: router.currentRoute.value.path,
    query: { ...router.currentRoute.value.query, [VITABLE_ADMIN_EMPLOYER_QUERY_KEY]: undefined },
    hash: router.currentRoute.value.hash
  })
}

async function clearBootstrapEmployerIdIfPresent(bootstrapEmployerId: string | null | "invalid") {
  if (!bootstrapEmployerId || bootstrapEmployerId === "invalid") return

  try {
    await clearBootstrapEmployerId()
  } catch (error) {
    Sentry.captureException(error, { tags: { context: "company_context_bootstrap_query_cleanup" } })
  }
}
